import SearchIcon from '@mui/icons-material/Search';
import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import debounce from 'lodash/debounce';
import * as React from 'react';
import { useLookUpTimeZone } from '../../../app/ApiGen';
import type { InputComponentProps } from '../../../design-system';
import type { Address } from '../../../utils/api';
import { useFormikContext } from '../../../utils/formUtils';
import { mapPlaceToAddress, parsePlace } from '../../../utils/googlePlace';
import type { LocationFields } from '../../../utils/locations/FieldDefinitions';
import { GoogleMapLoader } from '../../GoogleMap';
import { TextField } from '../../TextField';

type AddressSearchInputProps = Pick<InputComponentProps<Address>, 'disabled'> & {
  organisationId: string | undefined;
};

type Prediction = google.maps.places.AutocompletePrediction;

const DEFAULT_TIMEZONE = 'Pacific/Auckland';
const PREDICTIONS_DEBOUNCE_MS = 300;
const PLACE_DETAIL_FIELDS = ['address_components', 'geometry'];

const AddressAutocomplete: React.VFC<AddressSearchInputProps> = ({ disabled, organisationId }) => {
  const { setFieldValue } = useFormikContext<LocationFields>();

  const { refetch: lookUpTimeZone } = useLookUpTimeZone({ lazy: true, organisationId: organisationId ?? '' });

  const [inputValue, setInputValue] = React.useState('');
  const [predictions, setPredictions] = React.useState<Prediction[]>([]);

  const autocompleteService = React.useMemo(() => new google.maps.places.AutocompleteService(), []);
  const placesService = React.useMemo(() => new google.maps.places.PlacesService(document.createElement('div')), []);
  const sessionToken = React.useRef<google.maps.places.AutocompleteSessionToken>();

  const fetchPredictions = React.useMemo(
    () =>
      debounce((input: string) => {
        if (!input) {
          setPredictions([]);
          return;
        }
        if (!sessionToken.current) {
          sessionToken.current = new google.maps.places.AutocompleteSessionToken();
        }
        autocompleteService
          .getPlacePredictions({ input, sessionToken: sessionToken.current })
          .then(({ predictions: results }) => setPredictions(results ?? []))
          .catch(() => setPredictions([]));
      }, PREDICTIONS_DEBOUNCE_MS),
    [autocompleteService],
  );
  React.useEffect(() => () => fetchPredictions.cancel(), [fetchPredictions]);

  const applyPlace = React.useCallback(
    (result: google.maps.places.PlaceResult) => {
      const place = parsePlace(result);

      const mappedAddress = mapPlaceToAddress(place);
      setFieldValue({ key: 'address', value: mappedAddress });
      setFieldValue({
        key: 'installationPoint',
        value: {
          address: [mappedAddress.address1, mappedAddress.address2, mappedAddress.address3, mappedAddress.city]
            .filter(Boolean)
            .join(', '),
          countryShort: place.addressComponents.countryShort,
        },
      });

      if (!place.coordinates) {
        setFieldValue({ key: 'coordinates', value: { latitude: '', longitude: '' } });
        setFieldValue({ key: 'timeZone', value: '' });
        return;
      }

      const coordinates = {
        latitude: place.coordinates.latitude.toFixed(7),
        longitude: place.coordinates.longitude.toFixed(7),
      };
      setFieldValue({ key: 'coordinates', value: coordinates });

      lookUpTimeZone({ queryParams: coordinates })
        .then((response) => setFieldValue({ key: 'timeZone', value: response ? response.data : DEFAULT_TIMEZONE }))
        .catch((error) => console.error('Error looking up time zone', error));
    },
    [lookUpTimeZone, setFieldValue],
  );

  const onSelectPrediction = React.useCallback(
    (prediction: Prediction | null) => {
      if (!prediction) {
        return;
      }
      setInputValue(prediction.description);
      placesService.getDetails(
        { placeId: prediction.place_id, fields: PLACE_DETAIL_FIELDS, sessionToken: sessionToken.current },
        (result) => {
          sessionToken.current = undefined;
          if (result) {
            applyPlace(result);
          }
        },
      );
    },
    [applyPlace, placesService],
  );

  return (
    <Autocomplete<Prediction, false, false, false>
      disabled={disabled}
      filterOptions={(options) => options}
      forcePopupIcon={false}
      fullWidth
      getOptionLabel={(option) => option.description}
      inputValue={inputValue}
      noOptionsText="No matching addresses"
      onChange={(_event, value) => onSelectPrediction(value)}
      onInputChange={(_event, value, reason) => {
        setInputValue(value);
        if (reason === 'input') {
          fetchPredictions(value);
        } else if (reason === 'clear') {
          setPredictions([]);
        }
      }}
      options={predictions}
      size="small"
      value={null}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="inherit" fontSize="small" />
              </InputAdornment>
            ),
          }}
          disabled={params.disabled}
          fullWidth={params.fullWidth}
          id={params.id}
          inputElementProps={params.inputProps}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
          placeholder="Search for an address..."
          size={params.size}
          type="search"
        />
      )}
    />
  );
};

export const AddressEditorSearchInput: React.VFC<AddressSearchInputProps> = ({ disabled, organisationId }) => (
  <GoogleMapLoader>
    <AddressAutocomplete disabled={disabled} organisationId={organisationId} />
  </GoogleMapLoader>
);
